import { supabase } from '../supabaseClient';
import { homeownerRecords } from './homeownerApi';
import { storageErrorMessage } from './fileUpload';
import { compressTaskPhoto } from './taskPhotoFile';
import { dropCachedSignedUrl, signCached } from '../shared/signedUrlCache';
import { TASK_PHOTO_BUCKET, SIGNED_SECONDS, taskPhotoPath } from '../shared/projectTaskLimits';

function signTaskPhotos(paths) {
  return signCached(
    TASK_PHOTO_BUCKET,
    paths,
    SIGNED_SECONDS,
    (batch, seconds) => supabase.storage.from(TASK_PHOTO_BUCKET).createSignedUrls(batch, seconds)
  );
}

async function storeAgentTaskPhoto(projectId, taskKey, prepared, profileId) {
  const photoId = crypto.randomUUID();
  const path = taskPhotoPath(projectId, taskKey, photoId);
  const uploaded = await supabase.storage.from(TASK_PHOTO_BUCKET).upload(path, prepared, {
    contentType: 'image/jpeg',
    upsert: false,
  });
  if (uploaded.error) throw uploaded.error;
  const inserted = await supabase.from('project_task_photos').insert({
    id: photoId,
    project_id: projectId,
    task_key: String(taskKey),
    storage_path: path,
    content_type: 'image/jpeg',
    byte_size: prepared.size,
    uploaded_by_role: 'agent',
    uploaded_by: profileId || null,
  }).select('id, uploaded_by_role, uploaded_by').single();
  if (inserted.error) {
    await supabase.storage.from(TASK_PHOTO_BUCKET).remove([path]);
    throw inserted.error;
  }
  const signed = await signTaskPhotos([path]);
  const url = signed.data && signed.data[0] && signed.data[0].signedUrl;
  if (signed.error || !url) throw signed.error || new Error('Could not open that photo.');
  return {
    id: inserted.data.id,
    url,
    storage_path: path,
    uploaded_by_role: inserted.data.uploaded_by_role,
    uploaded_by: inserted.data.uploaded_by,
  };
}

export async function uploadAgentTaskPhoto(projectId, taskKey, file, profileId) {
  const saved = await uploadAgentTaskPhotos(projectId, taskKey, [file], profileId);
  return saved[0];
}

/**
 * Compress the next photo while the current one uploads.
 * Photos still save in the order they were picked.
 */
export async function uploadAgentTaskPhotos(projectId, taskKey, files, profileId, onPhoto) {
  const list = (files || []).filter(Boolean);
  const saved = [];
  let preparing = list.length ? compressTaskPhoto(list[0]) : null;
  for (let index = 0; index < list.length; index += 1) {
    const prepared = await preparing;
    if (list[index + 1]) preparing = compressTaskPhoto(list[index + 1]);
    const photo = await storeAgentTaskPhoto(projectId, taskKey, prepared, profileId);
    saved.push(photo);
    if (onPhoto) onPhoto(photo);
  }
  return saved;
}

export async function deleteAgentTaskPhoto(photo) {
  if (!photo || !photo.id) return;
  if (photo.storage_path) {
    dropCachedSignedUrl(TASK_PHOTO_BUCKET, photo.storage_path);
    await supabase.storage.from(TASK_PHOTO_BUCKET).remove([photo.storage_path]);
  }
  const removed = await supabase.from('project_task_photos').delete().eq('id', photo.id);
  if (removed.error) throw removed.error;
}

export async function loadAgentTaskPhotos(projectIds) {
  const ids = (projectIds || []).filter(Boolean);
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('project_task_photos')
    .select('id, project_id, task_key, storage_path, uploaded_by_role, uploaded_by, created_at')
    .in('project_id', ids)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = data || [];
  let signed = [];
  if (rows.length) {
    const result = await signTaskPhotos(rows.map((row) => row.storage_path));
    if (result.error) throw result.error;
    signed = result.data || [];
  }
  const byTask = {};
  rows.forEach((row, index) => {
    const key = `${row.project_id}:${row.task_key}`;
    if (!byTask[key]) byTask[key] = [];
    byTask[key].push({
      id: row.id,
      url: signed[index] && signed[index].signedUrl ? signed[index].signedUrl : '',
      storage_path: row.storage_path,
      uploaded_by_role: row.uploaded_by_role,
      uploaded_by: row.uploaded_by,
    });
  });
  return byTask;
}

/** Start shrinking a photo early. A failure still rejects when the upload awaits it. */
export function warmTaskPhoto(file) {
  if (!file) return null;
  const pending = compressTaskPhoto(file);
  pending.catch(() => {});
  return pending;
}

/**
 * The upload ticket does not need the bytes, so it is requested while
 * the photo shrinks. The next photo starts shrinking during the upload.
 * Confirm stays in order so the 12-photo limit still counts each save.
 */
export async function uploadHomeownerTaskPhotos(projectId, taskKey, files, onPhoto, firstPrepared) {
  const list = (files || []).filter(Boolean);
  const saved = [];
  let preparing = null;
  if (list.length) preparing = firstPrepared || compressTaskPhoto(list[0]);
  for (let index = 0; index < list.length; index += 1) {
    const ticketPromise = homeownerRecords({
      resource: 'project_photos',
      action: 'upload_url',
      project_id: projectId,
      task_key: String(taskKey),
    });
    const prepared = await preparing;
    if (list[index + 1]) preparing = compressTaskPhoto(list[index + 1]);
    const ticket = await ticketPromise;
    const uploadResponse = await fetch(ticket.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: await prepared.arrayBuffer(),
    });
    if (!uploadResponse.ok) throw new Error(await storageErrorMessage(uploadResponse, 'Could not upload that photo'));
    const savedPhoto = await homeownerRecords({
      resource: 'project_photos',
      action: 'confirm',
      project_id: projectId,
      task_key: String(taskKey),
      photo_id: ticket.photoId,
      path: ticket.path,
      byte_size: prepared.size,
    });
    saved.push(savedPhoto.photo);
    if (onPhoto) onPhoto(savedPhoto.photo);
  }
  return saved;
}

export async function uploadHomeownerTaskPhoto(projectId, taskKey, file) {
  const saved = await uploadHomeownerTaskPhotos(projectId, taskKey, [file]);
  return saved[0];
}

export async function deleteHomeownerTaskPhoto(projectId, taskKey, photoId) {
  await homeownerRecords({
    resource: 'project_photos',
    action: 'delete',
    project_id: projectId,
    task_key: String(taskKey),
    photo_id: photoId,
  });
}
